import React, { useState } from 'react';

const GamesPage: React.FC = () => {
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [gameResult, setGameResult] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);

  // Alphabet Game
  const [currentLetter, setCurrentLetter] = useState<string>('A');
  const [options, setOptions] = useState<string[]>(['Apple', 'Ball', 'Cat']);
  
  // Word Matching Game
  const [currentWord, setCurrentWord] = useState<string>('Cat');
  const [imageOptions, setImageOptions] = useState<string[]>(['🐱', '🐶', '🐰']);

  const selectGame = (game: string) => {
    setSelectedGame(game);
    setGameResult(null);
    setIsCorrect(null);
    
    if (game === 'alphabet') {
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const randomIndex = Math.floor(Math.random() * letters.length);
      const newLetter = letters[randomIndex];
      setCurrentLetter(newLetter);
      
      // Dictionary of words for each letter
      const wordOptions = {
        'A': ['Apple', 'Ant', 'Airplane'],
        'B': ['Ball', 'Banana', 'Book'],
        'C': ['Cat', 'Car', 'Cake'],
        'D': ['Dog', 'Duck', 'Door'],
        'E': ['Elephant', 'Egg', 'Eye'],
        'F': ['Fish', 'Flower', 'Frog'],
        'G': ['Goat', 'Grape', 'Guitar'],
        'H': ['House', 'Hat', 'Horse'],
        'I': ['Ice cream', 'Igloo', 'Insect'],
        'J': ['Juice', 'Jam', 'Jellyfish'],
        'K': ['Kite', 'King', 'Kangaroo'],
        'L': ['Lion', 'Lemon', 'Leaf'],
        'M': ['Monkey', 'Moon', 'Mouse'],
        'N': ['Nest', 'Nose', 'Nut'],
        'O': ['Orange', 'Owl', 'Ocean'],
        'P': ['Pencil', 'Pig', 'Pizza'],
        'Q': ['Queen', 'Question', 'Quilt'],
        'R': ['Rabbit', 'Rainbow', 'Robot'],
        'S': ['Sun', 'Star', 'Snake'],
        'T': ['Tree', 'Train', 'Tiger'],
        'U': ['Umbrella', 'Unicorn', 'Up'],
        'V': ['Violin', 'Van', 'Vegetable'],
        'W': ['Water', 'Window', 'Watch'],
        'X': ['Xylophone', 'X-ray', 'Box'],
        'Y': ['Yellow', 'Yo-yo', 'Yogurt'],
        'Z': ['Zebra', 'Zoo', 'Zipper']
      };
      
      // Get one correct option that starts with the current letter
      const correctOptions = wordOptions[newLetter as keyof typeof wordOptions] || ['Apple'];
      const correctOption = correctOptions[Math.floor(Math.random() * correctOptions.length)];
      
      // Get wrong options from other letters
      const wrongOptions: string[] = [];
      const otherLetters = letters.replace(newLetter, '').split('');
      
      // Randomly select 2 different letters for wrong options
      while (wrongOptions.length < 2 && otherLetters.length > 0) {
        const randomLetterIndex = Math.floor(Math.random() * otherLetters.length);
        const randomLetter = otherLetters[randomLetterIndex];
        
        // Remove the selected letter to avoid duplicates
        otherLetters.splice(randomLetterIndex, 1);
        
        const letterOptions = wordOptions[randomLetter as keyof typeof wordOptions];
        if (letterOptions && letterOptions.length > 0) {
          const randomWord = letterOptions[Math.floor(Math.random() * letterOptions.length)];
          wrongOptions.push(randomWord);
        }
      }
      
      // If we couldn't get enough wrong options, add some defaults
      if (wrongOptions.length < 2) {
        const defaultWrongOptions = ['Ball', 'Cat', 'Dog', 'Fish', 'House'];
        for (const option of defaultWrongOptions) {
          if (option.charAt(0) !== newLetter && wrongOptions.length < 2) {
            wrongOptions.push(option);
          }
        }
      }
      
      // Combine correct and wrong options and shuffle
      const allOptions = [correctOption, ...wrongOptions];
      const shuffledOptions = allOptions.sort(() => 0.5 - Math.random());
      
      setOptions(shuffledOptions);
    } else if (game === 'wordMatch') {
      const words = ['Cat', 'Dog', 'Rabbit', 'Fish', 'Bird'];
      const emojis = {
        'Cat': '🐱',
        'Dog': '🐶',
        'Rabbit': '🐰',
        'Fish': '🐠',
        'Bird': '🐦'
      };
      
      const randomIndex = Math.floor(Math.random() * words.length);
      const selectedWord = words[randomIndex];
      setCurrentWord(selectedWord);
      
      // Create array with correct emoji and two random ones
      const correctEmoji = emojis[selectedWord as keyof typeof emojis];
      const allEmojis = Object.values(emojis);
      const filteredEmojis = allEmojis.filter(emoji => emoji !== correctEmoji);
      
      // Shuffle and take 2 wrong options
      const shuffled = filteredEmojis.sort(() => 0.5 - Math.random());
      const wrongOptions = shuffled.slice(0, 2);
      
      // Combine and shuffle all options
      const allOptions = [correctEmoji, ...wrongOptions].sort(() => 0.5 - Math.random());
      setImageOptions(allOptions);
    }
  };

  const checkAlphabetAnswer = (word: string) => {
    const isCorrect = word.charAt(0).toUpperCase() === currentLetter;
    setIsCorrect(isCorrect);
    setGameResult(isCorrect ? 
      `أحسنت! ${word} تبدأ بحرف ${currentLetter}` : 
      `حاول مرة أخرى! ${word} لا تبدأ بحرف ${currentLetter}`);
    
    // Generate new question after a delay
    setTimeout(() => {
      selectGame('alphabet');
    }, 2000);
  };

  const checkWordMatch = (emoji: string) => {
    const emojis = {
      'Cat': '🐱',
      'Dog': '🐶',
      'Rabbit': '🐰',
      'Fish': '🐠',
      'Bird': '🐦'
    };
    
    const isCorrect = emoji === emojis[currentWord as keyof typeof emojis];
    setIsCorrect(isCorrect);
    setGameResult(isCorrect ? 
      `أحسنت! هذه صورة ${currentWord}` : 
      `حاول مرة أخرى! هذه ليست صورة ${currentWord}`);
    
    // Generate new question after a delay
    setTimeout(() => {
      selectGame('wordMatch');
    }, 2000);
  };

  return (
    <div className="section">
      <div className="container">
        <h1 className="section-title">الألعاب التعليمية</h1>
        
        {!selectedGame ? (
          <div className="card-container">
            <div className="card">
              <div className="card-content">
                <h3 className="card-title">لعبة الحروف</h3>
                <p className="card-text">تعلم الحروف الإنجليزية من خلال اختيار الكلمة التي تبدأ بالحرف المعروض.</p>
                <button onClick={() => selectGame('alphabet')} className="btn">العب الآن</button>
              </div>
            </div>
            
            <div className="card">
              <div className="card-content">
                <h3 className="card-title">لعبة مطابقة الكلمات</h3>
                <p className="card-text">اختر الصورة المناسبة للكلمة المعروضة.</p>
                <button onClick={() => selectGame('wordMatch')} className="btn">العب الآن</button>
              </div>
            </div>
            
            <div className="card">
              <div className="card-content">
                <h3 className="card-title">لعبة الألوان</h3>
                <p className="card-text">تعلم أسماء الألوان باللغة الإنجليزية بطريقة ممتعة.</p>
                <button className="btn" disabled>قريباً</button>
              </div>
            </div>
          </div>
        ) : selectedGame === 'alphabet' ? (
          <div className="game-container">
            <h2>لعبة الحروف</h2>
            <p>اختر الكلمة التي تبدأ بالحرف:</p>
            <h1 style={{ fontSize: '4rem', color: 'var(--primary-color)' }}>{currentLetter}</h1>
            
            <div>
              {options.map((word, index) => (
                <button 
                  key={index} 
                  className="game-button"
                  onClick={() => checkAlphabetAnswer(word)}
                >
                  {word}
                </button>
              ))}
            </div>
            
            {gameResult && (
              <p className={`game-result ${isCorrect ? 'correct' : 'incorrect'}`}>
                {gameResult}
              </p>
            )}
            
            <button onClick={() => setSelectedGame(null)} className="btn" style={{ marginTop: '20px' }}>
              العودة للألعاب
            </button>
          </div>
        ) : (
          <div className="game-container">
            <h2>لعبة مطابقة الكلمات</h2>
            <p>اختر الصورة المناسبة للكلمة:</p>
            <h1 style={{ fontSize: '3rem', color: 'var(--primary-color)' }}>{currentWord}</h1>
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
              {imageOptions.map((emoji, index) => (
                <button 
                  key={index} 
                  className="game-button"
                  style={{ fontSize: '3rem', padding: '10px 30px' }}
                  onClick={() => checkWordMatch(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
            
            {gameResult && (
              <p className={`game-result ${isCorrect ? 'correct' : 'incorrect'}`}>
                {gameResult}
              </p>
            )}
            
            <button onClick={() => setSelectedGame(null)} className="btn" style={{ marginTop: '20px' }}>
              العودة للألعاب
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GamesPage;
